import type { BaseRegistration, Dict, RegistrationDisposer } from 'piral-core'
import { buildName, type GlobalState, type PiralPlugin } from 'piral-core'
import type React from 'react'
import type { User } from '../../../api/user/types'
import * as actions from './actions'

declare module 'piral-core/lib/types/custom' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface PiletCustomApi extends UserMenuApi {}

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface PiralCustomState {}

  interface PiralCustomActions {
    registerUserTitleRenderer(name: string, value: UserTitleRendererRegistration): void
    unregisterUserTitleRenderer(name: string): void

    registerUserAction(name: string, value: UserActionRegistration): void
    unregisterUserAction(name: string): void
  }

  interface PiralCustomRegistryState {
    userTitleRendererItems: Dict<UserTitleRendererRegistration>
    userActionItems: Dict<UserActionRegistration>
  }
}

export interface UserTitleRendererRegistration extends BaseRegistration {
  title: (user: User<Role>) => string
}
export interface UserActionRegistration extends BaseRegistration {
  menuEntry: (buttonClassName: string) => React.ReactNode
}

export interface UserMenuApi {
  registerUserTitleRenderer(name: string, title: (user: User<Role>) => string): RegistrationDisposer
  unregisterUserTitleRenderer(name: string): void

  registerUserAction(name: string, menuEntry: (buttonClassName: string) => React.ReactNode): RegistrationDisposer
  unregisterUserAction(name: string): void
}

function withEmptyUserMenuExtensions() {
  return (state: GlobalState): GlobalState => ({
    ...state,
    registry: {
      ...state.registry,
      userTitleRendererItems: {},
      userActionItems: {}
    }
  })
}

export function createUserMenuApi(): PiralPlugin<UserMenuApi> {
  return (context) => {
    context.defineActions(actions)
    context.dispatch(withEmptyUserMenuExtensions())

    return (_api, target) => {
      const pilet = target.name
      let next = 0

      return {
        registerUserTitleRenderer(name, title): RegistrationDisposer {
          const internalName = name || next++
          const id = buildName(pilet, internalName)
          context.registerUserTitleRenderer(id, {
            pilet,
            title
          } satisfies UserTitleRendererRegistration)
          return () => context.unregisterTrayItem(id)
        },
        unregisterUserTitleRenderer(name) {
          const id = buildName(pilet, name)
          context.unregisterUserTitleRenderer(id)
        },

        registerUserAction(name, menuEntry): RegistrationDisposer {
          const internalName = name || next++
          const id = buildName(pilet, internalName)
          context.registerUserAction(id, {
            pilet,
            menuEntry
          } satisfies UserActionRegistration)
          return () => context.unregisterTrayItem(id)
        },
        unregisterUserAction(name) {
          const id = buildName(pilet, name)
          context.unregisterUserAction(id)
        }
      }
    }
  }
}
